/**
 * Typed client + pure editor-state helpers for the WP10 admin template editor
 * (`/api/admin/templates/**`, `docs/specs/WP10-template-editor.md`). Query/mutation shapes follow
 * the same conventions as `lib/rooms.ts`: plain `{queryKey, queryFn}` objects and one-argument
 * mutation functions that drop straight into `useMutation({mutationFn})`.
 *
 * `EditableTemplate`/`EditableSection`/`EditableField` are the in-memory shape the editor page
 * mutates (each section/field carries a stable `localId` for React keys and reordering, since a
 * brand-new section/field has no server id yet); `TemplateStructure` is the wire shape the `PUT`
 * endpoint accepts. `structureFromTemplate` converts a fetched `Template`/`AdminTemplate` into the
 * editable shape and `toTemplateStructure` is its inverse (dropping `localId`, used to build the
 * `PUT` body) — both are pure and unit-tested directly.
 */
import { apiGet, apiSend } from './api';
import type { AppliesTo, FieldConfig, FieldType } from './fieldValue';
import { fieldsBySection, type Template } from './templates';

export type TemplateState = 'DRAFT' | 'PUBLISHED' | 'RETIRED';

export interface AdminTemplateSummary {
  id: number;
  key: string;
  version: number;
  name: string;
  seriesLabel: string;
  state: TemplateState;
  roomCount: number;
  createdAt: string;
  seeded: boolean;
}

export interface AdminTemplate extends Template {
  state: TemplateState;
  roomCount: number;
  seeded: boolean;
}

// ---------------------------------------------------------------------------
// Wire shape for POST/PUT — mirrors the backend's `TemplateStructure` (WP10 spec).
// ---------------------------------------------------------------------------

export interface TemplateStructureField {
  key: string;
  label: string;
  type: FieldType;
  appliesTo: AppliesTo;
  allowFootnote: boolean;
  overlayGroup: string | null;
  exportColumn: string | null;
  config: FieldConfig;
}

export interface TemplateStructureSection {
  key: string;
  name: string;
  isChapter: boolean;
  fields: TemplateStructureField[];
}

export interface TemplateStructure {
  name: string;
  seriesLabel: string;
  sections: TemplateStructureSection[];
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const adminTemplatesQuery = {
  queryKey: ['admin', 'templates'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<AdminTemplateSummary[]>('/api/admin/templates', signal),
};

export function adminTemplateQuery(id: number) {
  return {
    queryKey: ['admin', 'templates', id] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<AdminTemplate>(`/api/admin/templates/${id}`, signal),
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface CreateAdminTemplateInput {
  key: string;
  name: string;
  seriesLabel: string;
  /** Copies sections/fields (and, when non-blank, defaults name/seriesLabel) from this template. */
  fromTemplateId?: number;
}

export function createAdminTemplate(input: CreateAdminTemplateInput): Promise<AdminTemplate> {
  return apiSend('POST', '/api/admin/templates', input);
}

export function saveAdminTemplate(vars: { id: number; structure: TemplateStructure }): Promise<AdminTemplate> {
  return apiSend('PUT', `/api/admin/templates/${vars.id}`, vars.structure);
}

export function publishAdminTemplate(vars: { id: number }): Promise<AdminTemplate> {
  return apiSend('POST', `/api/admin/templates/${vars.id}/publish`);
}

export function deleteAdminTemplate(vars: { id: number }): Promise<void> {
  return apiSend('DELETE', `/api/admin/templates/${vars.id}`);
}

// ---------------------------------------------------------------------------
// Editable structure — the editor page's local state shape.
// ---------------------------------------------------------------------------

export interface EditableField extends TemplateStructureField {
  localId: string;
}

export interface EditableSection extends Omit<TemplateStructureSection, 'fields'> {
  localId: string;
  fields: EditableField[];
}

export interface EditableTemplate {
  name: string;
  seriesLabel: string;
  sections: EditableSection[];
}

let localIdCounter = 0;

/** A `localId` unique for the lifetime of the page — never sent to the server, only used as a
 * React key and as the handle the editor's add/remove/move helpers below address sections/fields
 * by (a fetched section/field is keyed off its real server id instead, via `fromServerId`, so a
 * round trip through `structureFromTemplate` keeps stable keys across a save-and-refetch). */
function nextLocalId(prefix: string): string {
  localIdCounter += 1;
  return `${prefix}-new-${localIdCounter}`;
}

function fromServerId(prefix: string, id: number): string {
  return `${prefix}-${id}`;
}

/** `Template`/`AdminTemplate` (the fetched, server-shaped DTO) → the editable structure the editor
 * page mutates. Pure and total: any valid `Template` converts without throwing. */
export function structureFromTemplate(template: Template): EditableTemplate {
  return {
    name: template.name,
    seriesLabel: template.seriesLabel,
    sections: fieldsBySection(template).map(({ section, fields }) => ({
      localId: fromServerId('section', section.id),
      key: section.key,
      name: section.name,
      isChapter: section.isChapter,
      fields: fields.map((field) => ({
        localId: fromServerId('field', field.id),
        key: field.key,
        label: field.label,
        type: field.type,
        appliesTo: field.appliesTo,
        allowFootnote: field.allowFootnote,
        overlayGroup: field.overlayGroup,
        exportColumn: field.exportColumn,
        config: field.config,
      })),
    })),
  };
}

/** Inverse of `structureFromTemplate` (modulo `localId`/server ids, which the wire shape has no
 * room for): the `PUT`/`publish` request body for the editor's current state. */
export function toTemplateStructure(template: EditableTemplate): TemplateStructure {
  return {
    name: template.name,
    seriesLabel: template.seriesLabel,
    sections: template.sections.map((section) => ({
      key: section.key,
      name: section.name,
      isChapter: section.isChapter,
      fields: section.fields.map((field) => ({
        key: field.key,
        label: field.label,
        type: field.type,
        appliesTo: field.appliesTo,
        allowFootnote: field.allowFootnote,
        overlayGroup: field.overlayGroup,
        exportColumn: field.exportColumn,
        config: field.config,
      })),
    })),
  };
}

// ---------------------------------------------------------------------------
// Field-type defaults — used both for a brand-new field and when the type dropdown changes.
// ---------------------------------------------------------------------------

/** `IMAGE`/`LONG_TEXT`/`LINK` have no per-row cell, so the server requires `appliesTo = ROOM` for
 * them (WP10 spec's validation table) — mirrored here so the form never lets an admin build a
 * combination the server would reject. */
const ROOM_ONLY_TYPES: ReadonlySet<FieldType> = new Set(['IMAGE', 'LONG_TEXT', 'LINK']);

export function requiresRoomScope(type: FieldType): boolean {
  return ROOM_ONLY_TYPES.has(type);
}

export function defaultConfigForType(type: FieldType): FieldConfig {
  switch (type) {
    case 'TIER':
      return { type: 'TIER', tiers: ['SSS', 'S', 'A', 'B', 'C', 'D'] };
    case 'STARS':
      return { type: 'STARS', max: 5, labels: ['F', 'D', 'C', 'B', 'A', 'S'] };
    case 'DURATION':
      return { type: 'DURATION', maxSeconds: 60 };
    case 'RATIO':
      return { type: 'RATIO', leftLabel: 'Left', rightLabel: 'Right' };
    case 'NUMBER':
      return { type: 'NUMBER', min: '0', max: '100', step: '1' };
    case 'SHORT_TEXT':
      return { type: 'SHORT_TEXT', maxLength: 80 };
    case 'LONG_TEXT':
      return { type: 'LONG_TEXT', maxLength: 2000 };
    case 'IMAGE':
      return { type: 'IMAGE' };
    case 'LINK':
      return { type: 'LINK' };
  }
}

export function createField(): EditableField {
  return {
    localId: nextLocalId('field'),
    key: '',
    label: '',
    type: 'TIER',
    appliesTo: 'BOTH_ROWS',
    allowFootnote: true,
    overlayGroup: '',
    exportColumn: '',
    config: defaultConfigForType('TIER'),
  };
}

export function createSection(): EditableSection {
  return {
    localId: nextLocalId('section'),
    key: '',
    name: '',
    isChapter: false,
    fields: [],
  };
}

// ---------------------------------------------------------------------------
// Pure editor-state transitions. Each takes the current `EditableTemplate` and returns a new one
// (never mutates its argument), so the editor page just does `setTemplate(addSection(template))`.
// ---------------------------------------------------------------------------

export function addSection(template: EditableTemplate): EditableTemplate {
  return { ...template, sections: [...template.sections, createSection()] };
}

export function removeSection(template: EditableTemplate, sectionLocalId: string): EditableTemplate {
  return { ...template, sections: template.sections.filter((section) => section.localId !== sectionLocalId) };
}

export function moveSection(
  template: EditableTemplate,
  sectionLocalId: string,
  direction: 'up' | 'down',
): EditableTemplate {
  const index = template.sections.findIndex((section) => section.localId === sectionLocalId);
  const target = direction === 'up' ? index - 1 : index + 1;
  if (index < 0 || target < 0 || target >= template.sections.length) {
    return template;
  }
  const sections = [...template.sections];
  const moved = sections[index]!;
  sections.splice(index, 1);
  sections.splice(target, 0, moved);
  return { ...template, sections };
}

export function updateSection(
  template: EditableTemplate,
  sectionLocalId: string,
  patch: Partial<Pick<EditableSection, 'key' | 'name' | 'isChapter'>>,
): EditableTemplate {
  return {
    ...template,
    sections: template.sections.map((section) =>
      section.localId === sectionLocalId ? { ...section, ...patch } : section,
    ),
  };
}

export function addField(template: EditableTemplate, sectionLocalId: string): EditableTemplate {
  return {
    ...template,
    sections: template.sections.map((section) =>
      section.localId === sectionLocalId ? { ...section, fields: [...section.fields, createField()] } : section,
    ),
  };
}

export function removeField(template: EditableTemplate, sectionLocalId: string, fieldLocalId: string): EditableTemplate {
  return {
    ...template,
    sections: template.sections.map((section) =>
      section.localId === sectionLocalId
        ? { ...section, fields: section.fields.filter((field) => field.localId !== fieldLocalId) }
        : section,
    ),
  };
}

export function moveField(
  template: EditableTemplate,
  sectionLocalId: string,
  fieldLocalId: string,
  direction: 'up' | 'down',
): EditableTemplate {
  return {
    ...template,
    sections: template.sections.map((section) => {
      if (section.localId !== sectionLocalId) {
        return section;
      }
      const index = section.fields.findIndex((field) => field.localId === fieldLocalId);
      const target = direction === 'up' ? index - 1 : index + 1;
      if (index < 0 || target < 0 || target >= section.fields.length) {
        return section;
      }
      const fields = [...section.fields];
      const moved = fields[index]!;
      fields.splice(index, 1);
      fields.splice(target, 0, moved);
      return { ...section, fields };
    }),
  };
}

/** Moves a field to another section, appending it at the end. No-op if the field or either section
 * can't be found, or if source and destination are the same. */
export function moveFieldToSection(
  template: EditableTemplate,
  fromSectionLocalId: string,
  fieldLocalId: string,
  toSectionLocalId: string,
): EditableTemplate {
  if (fromSectionLocalId === toSectionLocalId) {
    return template;
  }
  const fromSection = template.sections.find((section) => section.localId === fromSectionLocalId);
  const field = fromSection?.fields.find((candidate) => candidate.localId === fieldLocalId);
  const toSectionExists = template.sections.some((section) => section.localId === toSectionLocalId);
  if (!field || !toSectionExists) {
    return template;
  }
  return {
    ...template,
    sections: template.sections.map((section) => {
      if (section.localId === fromSectionLocalId) {
        return { ...section, fields: section.fields.filter((candidate) => candidate.localId !== fieldLocalId) };
      }
      if (section.localId === toSectionLocalId) {
        return { ...section, fields: [...section.fields, field] };
      }
      return section;
    }),
  };
}

export function updateField(
  template: EditableTemplate,
  sectionLocalId: string,
  fieldLocalId: string,
  patch: Partial<Omit<EditableField, 'localId'>>,
): EditableTemplate {
  return {
    ...template,
    sections: template.sections.map((section) =>
      section.localId === sectionLocalId
        ? {
            ...section,
            fields: section.fields.map((field) => (field.localId === fieldLocalId ? { ...field, ...patch } : field)),
          }
        : section,
    ),
  };
}

/** Changing a field's type resets its config to that type's default and, for the ROOM-only types,
 * forces `appliesTo = ROOM` and clears `overlayGroup`/`exportColumn` — mirrors the server's
 * validation rule (WP10 spec) so the form never submits a combination it would 400 on. */
export function changeFieldType(
  template: EditableTemplate,
  sectionLocalId: string,
  fieldLocalId: string,
  type: FieldType,
): EditableTemplate {
  return updateField(template, sectionLocalId, fieldLocalId, {
    type,
    config: defaultConfigForType(type),
    ...(requiresRoomScope(type) ? { appliesTo: 'ROOM', overlayGroup: null, exportColumn: null } : {}),
  });
}

/** Changing `appliesTo` to `ROOM` clears `overlayGroup`/`exportColumn` (the server requires both
 * null for ROOM-scoped fields); changing away from `ROOM` leaves them as-is for the admin to fill
 * in — they default to `null`, not a value that would already pass validation. */
export function changeFieldAppliesTo(
  template: EditableTemplate,
  sectionLocalId: string,
  fieldLocalId: string,
  appliesTo: AppliesTo,
): EditableTemplate {
  return updateField(template, sectionLocalId, fieldLocalId, {
    appliesTo,
    ...(appliesTo === 'ROOM' ? { overlayGroup: null, exportColumn: null } : {}),
  });
}

/**
 * `overlayGroups` (`lib/templates.ts`) equivalent for the editor's in-progress `EditableTemplate` —
 * a brand-new field has no server id yet, so it can't be fed through that helper's `Template` shape.
 * Same semantics: distinct non-null `overlayGroup` values, first-occurrence order, in template-wide
 * field order (section order, then field order within a section). Used by the editor's read-only
 * preview panel.
 */
export function distinctOverlayGroups(template: EditableTemplate): string[] {
  const seen = new Set<string>();
  const groups: string[] = [];
  for (const section of template.sections) {
    for (const field of section.fields) {
      const overlayGroup = field.overlayGroup;
      if (overlayGroup != null && overlayGroup.trim() !== '' && !seen.has(overlayGroup)) {
        seen.add(overlayGroup);
        groups.push(overlayGroup);
      }
    }
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Server validation error mapping — `ApiError.fields` uses path-style keys like
// `sections[2].fields[0].key` (WP10 spec); the editor renders each next to its input.
// ---------------------------------------------------------------------------

export interface ParsedValidationErrors {
  /** Template-level keys (`name`, `seriesLabel`) with no `sections[...]` prefix. */
  top: Record<string, string>;
  /** Section-level keys (`key`, `name`), by section index. */
  sections: Record<number, Record<string, string>>;
  /** Field-level keys, by `${sectionIndex}:${fieldIndex}`. */
  fields: Record<string, Record<string, string>>;
}

const FIELD_PATH = /^sections\[(\d+)]\.fields\[(\d+)]\.(.+)$/;
const SECTION_PATH = /^sections\[(\d+)]\.(.+)$/;

export function parseValidationErrors(fields: Record<string, string>): ParsedValidationErrors {
  const result: ParsedValidationErrors = { top: {}, sections: {}, fields: {} };
  for (const [path, message] of Object.entries(fields)) {
    const fieldMatch = FIELD_PATH.exec(path);
    if (fieldMatch) {
      const [, sectionIndex, fieldIndex, key] = fieldMatch;
      const bucket = `${sectionIndex}:${fieldIndex}`;
      result.fields[bucket] = { ...result.fields[bucket], [key!]: message };
      continue;
    }
    const sectionMatch = SECTION_PATH.exec(path);
    if (sectionMatch) {
      const [, sectionIndex, key] = sectionMatch;
      const index = Number(sectionIndex);
      result.sections[index] = { ...result.sections[index], [key!]: message };
      continue;
    }
    result.top[path] = message;
  }
  return result;
}

/** Section/field keys are `^[a-z0-9][a-z0-9_-]{0,39}$` (underscore-separated, per the WP10 spec's
 * validation table) — distinct from the template-level key, which uses hyphens. Falls back to
 * `'section'`/`'field'` (via the caller's own fallback) when the name has no slug-able characters. */
export function slugifyKey(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}
